import React from 'react';
import { Box, Drawer, IconButton, Typography, Divider, useTheme, useMediaQuery } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { adminColors, TOUCH_HIT_SX } from '../../../theme/adminTheme';

// Right-hand detail/edit panel shared across the admin console (a
// student's record, a class's roster, a safety flag's detail, ...) — one
// Drawer implementation so open/close motion, Esc-to-close, and focus
// handling are consistent everywhere instead of each view rolling its own
// Dialog or Drawer.
//
// Esc-to-close and the backdrop click are both free: MUI's Drawer is built
// on Modal, which already binds Escape (unless `disableEscapeKeyDown` is
// set, which this never does) and backdrop clicks to `onClose`, and moves
// focus into the panel on open / back to the trigger element on close.
// `autoFocus` on the close button below just picks *where* inside the
// panel that initial focus lands, rather than leaving it on the Paper root.
export default function SideDrawer({
  open,
  onClose,
  title,
  subtitle,
  headerActions,
  children,
  footer,
  width = 480,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = 'side-drawer-title';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : width,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 3,
          py: 2.5,
          // Matches the AppBar's own header border elsewhere in
          // AdminDashboardV2.js — adminTheme has no token for this specific
          // hairline yet (it's a light-surface border, distinct from
          // adminColors.sidebarBorder, which is the *dark* sidebar's).
          borderBottom: '1px solid rgba(24,24,27,0.08)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography id={titleId} variant="h6" component="h2" sx={{ fontWeight: 700, color: adminColors.ink }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {headerActions}
          <IconButton
            onClick={onClose}
            aria-label="Close panel"
            autoFocus
            sx={{
              ...TOUCH_HIT_SX,
              color: adminColors.inkSoft,
              '&:hover': { color: adminColors.ink, backgroundColor: adminColors.surface },
              '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>{children}</Box>

      {footer && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>{footer}</Box>
        </>
      )}
    </Drawer>
  );
}
